import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import EditMemberForm from "../../sections/@dashboard/members/EditMemberForm";
import {
  useDeleteMemberMutation,
  useUpdateMemberMutation,
} from "../../features/members/membersApiSlice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import Loading from "../spinners/Loading";
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  Grid,
  Typography,
} from "@mui/material";
import MemberDetails from "../../sections/@dashboard/members/MemberDetails";
import EditContractForm from "../../sections/@dashboard/members/EditContractForm";
import { useUpdateContractMutation } from "../../features/contracts/contractsApiSlice";
import TerminateMemberDialog from "./TerminateMemberDialog";

const EditMember = ({ member, handleClose }) => {
  const formRef = useRef();
  const [updateMember, { isLoading, isError }] = useUpdateMemberMutation();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState("");

  const getErrorMessage = (error) => {
    const status = error?.status ?? error?.originalStatus;
    if (status === 401 || status === 403) {
      return "Keine Berechtigung: Mitgliedsdaten dürfen von Ihrem Konto nicht geändert werden.";
    }
    return (
      error?.data?.message ||
      "Es gab ein Problem beim Ändern des Mitglieds. Bitte probieren Sie es erneut."
    );
  };

  const handleEditMember = async (data) => {
    try {
      setErrorMessage("");
      let result = await updateMember(data).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: result?.name + " wurde erfolgreich geändert!",
            type: "SUCCESS",
            data: {
              successText: result?.name + " wurde erfolgreich geändert!",
              refetchOnClose: false,
            },
          })
        );
      }
    } catch (error) {
      console.error("error :>> ", error);
      setErrorMessage(getErrorMessage(error));
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 220 }}>
        <Loading />
      </div>
    );
  }

  return (
    <>
      <DialogContent>
        {(isError || errorMessage) && (
          <Alert severity="error">
            {errorMessage ||
              "Es gab ein Problem beim Ändern des Mitglieds. Bitte probieren Sie es erneut."}
          </Alert>
        )}
        <EditMemberForm
          memberData={member}
          handleEditMember={handleEditMember}
          reference={formRef}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="error"
          disabled={isLoading}
          onClick={() => handleClose("CANCEL")}
        >
          Abbrechen
        </Button>
        <Button
          onClick={() => formRef.current.submitForm()}
          disabled={isLoading}
          variant="contained"
          color="primary"
        >
          Speichern
        </Button>
      </DialogActions>
    </>
  );
};

const Contract = ({ member, handleClose }) => {
  const formRef = useRef();
  const [updateContract, { isLoading, isError }] = useUpdateContractMutation();
  const dispatch = useDispatch();

  const handleEditContract = async (data) => {
    try {
      let result = await updateContract(data).unwrap();
      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "Der Vertrag wurde erfolgreich geändert!",
            type: "SUCCESS",
            data: {
              successText: "Der Vertrag wurde erfolgreich geändert!",
              refetchOnClose: false,
            },
          })
        );
      }
    } catch (error) {
      console.error("error :>> ", error);
    }
  };

  return (
    <>
      <DialogContent>
        {isError && (
          <Alert severity="error">
            Es gab ein Problem beim ändern des Vertrags. Bitte probieren Sie es
            erneut.
          </Alert>
        )}
        {isLoading && (
          <div style={{ padding: 220 }}>
            <Loading />
          </div>
        )}
        <EditContractForm
          member={member}
          handleEditContract={handleEditContract}
          reference={formRef}
        />
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="error"
          disabled={isLoading}
          onClick={() => handleClose("CANCEL")}
        >
          Abbrechen
        </Button>
        <Button
          onClick={() => formRef.current.submitForm()}
          disabled={isLoading}
          variant="contained"
          color="primary"
        >
          Speichern
        </Button>
      </DialogActions>
    </>
  );
};

const FreezeMember = () => {
  return <div>FreezeMember</div>;
};

const DeleteMember = ({ member, handleClose }) => {
  const [checked, setChecked] = useState(true);
  const dispatch = useDispatch();
  const [deleteMember, { isLoading, isError }] = useDeleteMemberMutation();

  const handleChange = (event) => {
    setChecked(event.target.checked);
  };

  const handleDeleteMember = async () => {
    // Check if user wants to delete contract too
    try {
      let result = await deleteMember({
        member,
        deleteContract: checked,
      }).unwrap();
      if (result.type === "SUCCESS") {
        dispatch(
          setDialog({
            title: "Mitglied wurde erfolgreich gelöscht!",
            type: "SUCCESS",
            data: {
              successText: `Mitglied${
                checked ? " und Vertrag wurden " : "wurde "
              }erfolgreich gelöscht!`,
              refetchOnClose: false,
            },
          })
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
    }
  };

  return (
    <>
      <DialogContent>
        {isLoading ? (
          <div style={{ padding: 200 }}>
            <Loading />
          </div>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h3">
                Möchten Sie das Mitglied wirklich löschen?
              </Typography>
              <Typography variant="body1">
                Diese Aktion kann nicht rückgängig gemacht werden. Die Daten
                werden entgültig gelöscht.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h4">
                Mitglied: {member.firstName + " " + member.lastName}
              </Typography>
            </Grid>
            {member?.contractRef?.hasMembers.length === 1 && (
              <Grid item xs={12}>
                <Typography variant="h6">
                  Wenn Sie dieses Mitglied löschen befinden sich im Vertrag
                  keine Mitglieder mehr.
                </Typography>
                <Typography variant="h6">
                  Möchten Sie auch den Vertrag löschen?
                </Typography>

                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="error"
                        checked={checked}
                        onChange={handleChange}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    }
                    label={<b>Vertrag löschen</b>}
                  />
                </FormGroup>
              </Grid>
            )}
          </Grid>
        )}
        {isError && (
          <Alert severity="error">
            Es gab ein Problem beim löschen des Mitglieds. Bitte probieren Sie
            es erneut.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="error"
          disabled={isLoading}
          onClick={() => handleClose("CANCEL")}
        >
          Abbrechen
        </Button>
        <Button
          onClick={handleDeleteMember}
          disabled={isLoading}
          variant="contained"
          color="error"
        >
          Mitglied Löschen
        </Button>
      </DialogActions>
    </>
  );
};

function MemberActions({ action, member, handleClose }) {
  let content = "";
  switch (action) {
    case "EDIT":
      content = <EditMember member={member} handleClose={handleClose} />;

      break;
    case "SHOW":
      content = <MemberDetails member={member} />;

      break;
    case "CONTRACT":
      content = <Contract member={member} handleClose={handleClose} />;

      break;
    case "TERMINATE":
      content = (
        <TerminateMemberDialog member={member} handleClose={handleClose} />
      );

      break;
    case "FREEZE":
      content = <FreezeMember member={member} />;

      break;

    case "DELETE":
      content = <DeleteMember member={member} handleClose={handleClose} />;

      break;

    default:
      break;
  }

  return <div>{content}</div>;
}

MemberActions.propTypes = {
  action: PropTypes.string,
  member: PropTypes.object,
};

export default MemberActions;
